// The handful of facts a brand repeats, and how a caption refers to them.
//
// Two kinds of hole appear in caption text and they mean opposite things:
//
//   {{booking}}   we know this. It is filled in for you when you pick the
//                 caption in Compose, from the brand's details.
//   [your offer]  we do not know this, and nobody can know it for you. It is
//                 there to be noticed and written over.
//
// Different brackets because the difference matters at exactly the moment
// somebody is about to publish: one of these is finished and one is not.
// Curly is ours, square is yours, and a caption that still has square brackets
// in it is a caption that is not ready.
//
// Resolution happens on the way INTO the composer, never on the way into the
// table. A draft stores `{{booking}}` for as long as it exists, so changing the
// booking link changes every future post that mentions it and none of the ones
// already published. Substituting at save time would freeze the value at the
// moment somebody happened to write the caption, which is the behaviour this
// whole feature exists to remove.

/**
 * The same shape the key column enforces, so a token that parses here is a
 * token the database would have accepted. Whitespace inside the braces is
 * tolerated because people type `{{ booking }}`.
 */
const TOKEN = /\{\{\s*([a-z][a-z0-9_]{0,31})\s*\}\}/g;

export interface SuggestedDetail {
  key: string;
  label: string;
  example: string;
  sort: number;
  /** Offered on the form that creates a brand. See `NEW_BRAND_DETAILS`. */
  onNewBrand?: boolean;
}

/** What the editor offers before anyone has invented their own. */
export const SUGGESTED_DETAILS: SuggestedDetail[] = [
  { key: "business", label: "Business name", example: "Hello Physio", sort: 5, onNewBrand: true },
  { key: "tagline", label: "Tagline", example: "Physio that comes to you", sort: 8, onNewBrand: true },
  { key: "website", label: "Website", example: "https://hellophysio.in", sort: 10, onNewBrand: true },
  { key: "booking", label: "Booking link", example: "https://hellophysio.in/book", sort: 20 },
  { key: "phone", label: "Phone", example: "+91 98765 43210", sort: 30, onNewBrand: true },
  { key: "whatsapp", label: "WhatsApp", example: "https://wa.me/919876543210", sort: 40, onNewBrand: true },
  { key: "email", label: "Email", example: "hello@example.com", sort: 50 },
  { key: "address", label: "Address", example: "12 MG Road, Bengaluru 560001", sort: 60 },
  { key: "hours", label: "Opening hours", example: "Mon–Sat, 8am–7pm", sort: 70 },
  { key: "handle", label: "Social handle", example: "@hellophysio", sort: 80 },
  { key: "hashtags", label: "Usual hashtags", example: "#physio #bengaluru", sort: 85 },
  // The two store links are one detail each rather than one "app link", because
  // a post that says "get it on Android" and links an App Store page is a post
  // that had one hole and filled it with the wrong half.
  { key: "play_store", label: "Google Play link",
    example: "https://play.google.com/store/apps/details?id=in.hellophysio", sort: 90,
    onNewBrand: true },
  { key: "app_store", label: "Apple App Store link",
    example: "https://apps.apple.com/app/id0000000000", sort: 91, onNewBrand: true },
];

/**
 * The fields the "add a brand" form puts on screen, in order.
 *
 * A subset and not the whole list: a form with twelve boxes on it is a form
 * people abandon, and everything left out is one dropdown away on the caption
 * library afterwards. These seven are the ones a business knows by heart on the
 * day it is set up.
 */
export const NEW_BRAND_DETAILS: SuggestedDetail[] =
  SUGGESTED_DETAILS.filter((s) => s.onNewBrand);

/** Every distinct token in the text, in the order it first appears. */
export function tokensIn(text: string): string[] {
  const seen: string[] = [];
  for (const m of text.matchAll(TOKEN)) {
    if (!seen.includes(m[1])) seen.push(m[1]);
  }
  return seen;
}

/** The `[like this]` holes, which nothing can fill but a person. */
export function blanksIn(text: string): string[] {
  const seen: string[] = [];
  for (const m of text.matchAll(/\[([^\]\n]{1,60})\]/g)) {
    if (!seen.includes(m[1])) seen.push(m[1]);
  }
  return seen;
}

export interface Resolved {
  text: string;
  /** Keys that were replaced. */
  filled: string[];
  /** Keys with no value on this brand. Left in the text, verbatim. */
  missing: string[];
}

/**
 * Fill what we can and leave the rest alone.
 *
 * An unknown token stays as `{{whatever}}` rather than becoming an empty
 * string, because a caption with a visible hole in it gets fixed and a caption
 * with a silent gap gets published.
 */
export function resolveTokens(text: string, details: Map<string, string>): Resolved {
  const filled: string[] = [];
  const missing: string[] = [];

  const out = text.replace(TOKEN, (whole, key: string) => {
    const value = details.get(key);
    if (value === undefined) {
      if (!missing.includes(key)) missing.push(key);
      return whole;
    }
    if (!filled.includes(key)) filled.push(key);
    return value;
  });

  return { text: out, filled, missing };
}

/**
 * The details that apply to one brand: its own, plus the workspace's shared
 * ones for every name it does not answer itself.
 *
 * `brand_id` null means shared, not unfiled (20260918000300). sabu's `{{phone}}`
 * wins over the workspace's `{{phone}}`; the workspace's `{{support_email}}`
 * still applies to sabu because sabu has none.
 *
 * Generic over the row rather than typed to `BrandDetail`, so this file stays
 * what it is — the token rules, with no dependency on the table.
 *
 * `brandId` null is "no brand chosen", and then only the shared rows apply: a
 * screen showing every brand at once must not silently resolve a caption using
 * whichever business happened to sort first.
 */
export function effectiveDetails<T extends { key: string; brand_id: string | null }>(
  details: T[], brandId: string | null,
): T[] {
  const out = new Map<string, T>();
  for (const d of details) {
    if (d.brand_id === null) {
      if (!out.has(d.key)) out.set(d.key, d);
    } else if (d.brand_id === brandId) {
      out.set(d.key, d);
    }
  }
  return [...out.values()];
}

/** What a key has to look like to be usable in `{{ }}`. Mirrors the check constraint. */
export const KEY_SHAPE = /^[a-z][a-z0-9_]{0,31}$/;

export function keyProblem(key: string): string | null {
  if (!key) return "Give it a name.";
  if (!KEY_SHAPE.test(key)) {
    return "Lowercase letters, numbers and underscores, starting with a letter — " +
           "it has to read cleanly inside {{ }}.";
  }
  return null;
}
